import { create } from 'zustand';

export const usePostsStore = create((set, get) => ({
  posts: [],
  isLoading: false,
  hasMore: true,
  offset: 0,
  limit: 10,
  calendarDates: [],
  searchResults: [],
  selectedDate: null,
  selectedPostId: null,
  selectedPostHasReply: false, // True if jumped to post because of reply search result

  // Fetch posts (paginated, with infinite scroll)
  fetchPosts: async (accessToken, reset = false, profileId = null) => {
    const state = get();

    if (reset) {
      set({ posts: [], offset: 0, hasMore: true });
    }

    if (!state.hasMore && !reset) return;

    set({ isLoading: true });

    try {
      let url;

      // If we have posts and we're in jump mode (selectedDate or selectedPostId),
      // fetch older posts based on last post's timestamp and ID
      if (!reset && state.posts.length > 0 && (state.selectedDate || state.selectedPostId)) {
        const lastPost = state.posts[state.posts.length - 1];
        url = profileId
          ? `/api/posts?before=${encodeURIComponent(lastPost.created_at)}&before_id=${lastPost.id}&limit=${state.limit}&profile_id=${profileId}`
          : `/api/posts?before=${encodeURIComponent(lastPost.created_at)}&before_id=${lastPost.id}&limit=${state.limit}`;
      } else {
        // Normal offset-based pagination
        const currentOffset = reset ? 0 : state.offset;
        url = profileId
          ? `/api/posts?offset=${currentOffset}&limit=${state.limit}&profile_id=${profileId}`
          : `/api/posts?offset=${currentOffset}&limit=${state.limit}`;
      }

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        // Filter out any duplicates by ID (shouldn't happen, but be safe)
        const existingIds = new Set(state.posts.map(p => p.id));
        const newUniquePosts = data.posts.filter(p => !existingIds.has(p.id));

        const newPosts = reset ? data.posts : [...state.posts, ...newUniquePosts];
        set({
          posts: newPosts,
          hasMore: data.has_more,
          offset: newPosts.length,
          isLoading: false
        });
      } else {
        console.error('Failed to fetch posts:', data.message);
        set({ isLoading: false });
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
      set({ isLoading: false });
    }
  },

  // Create new post
  createPost: async (accessToken, postData, profileId = null) => {
    try {
      const response = await fetch('/api/posts', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });

      const data = await response.json();

      if (data.success) {
        // Refresh posts to show new post at top (for the profile we just posted on)
        await get().fetchPosts(accessToken, true, profileId || postData.post_on_profile_id);
        return { success: true, post: data.post };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error creating post:', error);
      return { success: false, message: 'Failed to create post' };
    }
  },

  // Update post
  updatePost: async (accessToken, postId, postData) => {
    try {
      const response = await fetch(`/api/posts/${postId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });

      const data = await response.json();

      if (data.success) {
        // Update post in local state
        set(state => ({
          posts: state.posts.map(post =>
            post.id === postId ? { ...post, ...data.post } : post
          )
        }));
        return { success: true, post: data.post };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error updating post:', error);
      return { success: false, message: 'Failed to update post' };
    }
  },

  // Delete post
  deletePost: async (accessToken, postId) => {
    try {
      const response = await fetch(`/api/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        // Remove post from local state
        set(state => ({
          posts: state.posts.filter(post => post.id !== postId)
        }));
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      return { success: false, message: 'Failed to delete post' };
    }
  },

  // Toggle pin status
  togglePin: async (accessToken, postId) => {
    try {
      const response = await fetch(`/api/posts/${postId}/pin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        // Refresh posts to show updated pin order
        await get().fetchPosts(accessToken, true);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error toggling pin:', error);
      return { success: false, message: 'Failed to toggle pin' };
    }
  },

  // Reorder pinned posts
  reorderPins: async (accessToken, postIds) => {
    try {
      const response = await fetch('/api/posts/reorder-pins', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ post_ids: postIds })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh posts to show new order
        await get().fetchPosts(accessToken, true);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error reordering pins:', error);
      return { success: false, message: 'Failed to reorder pins' };
    }
  },

  // React to post
  reactToPost: async (accessToken, postId, likeValue = null, dislikeValue = null) => {
    try {
      const response = await fetch(`/api/posts/${postId}/react`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ like_value: likeValue, dislike_value: dislikeValue })
      });

      const data = await response.json();

      if (data.success) {
        // Update post reactions in local state
        set(state => ({
          posts: state.posts.map(post =>
            post.id === postId
              ? {
                  ...post,
                  total_likes: data.total_likes,
                  total_dislikes: data.total_dislikes,
                  user_like_value: likeValue,
                  user_dislike_value: dislikeValue
                }
              : post
          )
        }));
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error reacting to post:', error);
      return { success: false, message: 'Failed to react to post' };
    }
  },

  // Remove reaction from post or reply
  removeReaction: async (accessToken, postId, replyId = null) => {
    try {
      const url = replyId
        ? `/api/posts/${postId}/replies/${replyId}/react`
        : `/api/posts/${postId}/react`;

      console.log('removeReaction API call:', { url, method: 'DELETE', replyId });

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      console.log('removeReaction response status:', response.status);
      const data = await response.json();
      console.log('removeReaction response data:', data);

      if (data.success) {
        if (replyId) {
          console.log('Refreshing replies after removing reply reaction');
          // Refresh replies to get updated reaction counts
          await get().fetchReplies(accessToken, postId);
        } else {
          // Update post reactions in local state
          set(state => ({
            posts: state.posts.map(post =>
              post.id === postId
                ? {
                    ...post,
                    total_likes: data.total_likes,
                    total_dislikes: data.total_dislikes,
                    user_like_value: null,
                    user_dislike_value: null
                  }
                : post
            )
          }));
        }
        return { success: true };
      } else {
        console.error('removeReaction API returned success=false:', data.message);
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error removing reaction:', error);
      return { success: false, message: 'Failed to remove reaction' };
    }
  },

  // Fetch replies for a post
  fetchReplies: async (accessToken, postId) => {
    try {
      console.log('fetchReplies called for postId:', postId);
      const response = await fetch(`/api/posts/${postId}/replies`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();
      console.log('fetchReplies response - replies count:', data.replies?.length);
      console.log('fetchReplies response - reply data:', JSON.stringify(data.replies, null, 2));

      if (data.success) {
        // Update post with replies in local state
        set(state => ({
          posts: state.posts.map(post =>
            post.id === postId ? { ...post, replies: data.replies } : post
          )
        }));
        console.log('fetchReplies - updated store with replies');
        return { success: true, replies: data.replies };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error fetching replies:', error);
      return { success: false, message: 'Failed to fetch replies' };
    }
  },

  // Create reply
  createReply: async (accessToken, postId, replyData) => {
    try {
      const response = await fetch(`/api/posts/${postId}/replies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(replyData)
      });

      const data = await response.json();

      if (data.success) {
        // Refresh replies for this post
        await get().fetchReplies(accessToken, postId);
        return { success: true, reply: data.reply };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error creating reply:', error);
      return { success: false, message: 'Failed to create reply' };
    }
  },

  // Delete reply
  deleteReply: async (accessToken, postId, replyId) => {
    try {
      const response = await fetch(`/api/posts/${postId}/replies/${replyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      const data = await response.json();

      if (data.success) {
        // Refresh replies for this post
        await get().fetchReplies(accessToken, postId);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
      return { success: false, message: 'Failed to delete reply' };
    }
  },

  // React to reply
  reactToReply: async (accessToken, postId, replyId, likeValue = null, dislikeValue = null) => {
    try {
      const response = await fetch(`/api/posts/${postId}/replies/${replyId}/react`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ like_value: likeValue, dislike_value: dislikeValue })
      });

      const data = await response.json();

      if (data.success) {
        // Refresh replies to get updated reaction counts
        await get().fetchReplies(accessToken, postId);
        return { success: true };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error reacting to reply:', error);
      return { success: false, message: 'Failed to react to reply' };
    }
  },

  // Get tag suggestions for @mentions
  getTagSuggestions: async (accessToken, postId, search) => {
    try {
      // Use different endpoint based on whether we have a postId
      const endpoint = postId
        ? `/api/posts/${postId}/tag-suggestions?search=${encodeURIComponent(search)}`
        : `/api/posts/tag-suggestions?search=${encodeURIComponent(search)}`;

      console.log('PostsStore - Fetching from endpoint:', endpoint);

      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      console.log('PostsStore - Response status:', response.status);
      console.log('PostsStore - Response ok:', response.ok);

      const data = await response.json();
      console.log('PostsStore - Response data:', data);

      if (data.success) {
        return { success: true, suggestions: data.suggestions };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('Error fetching tag suggestions - FULL ERROR:', error);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      return { success: false, message: 'Failed to fetch suggestions' };
    }
  },

  // Fetch calendar data
  fetchCalendarData: async (accessToken, profileId) => {
    try {
      const url = `/api/posts/calendar?profile_id=${profileId}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      const data = await response.json();
      if (data.success) {
        set({ calendarDates: data.dates });
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    }
  },

  // Search posts and replies
  searchPosts: async (accessToken, query, profileId) => {
    try {
      const url = `/api/posts/search?query=${encodeURIComponent(query)}&profile_id=${profileId}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      const data = await response.json();
      if (data.success) {
        set({ searchResults: data.results });
      }
    } catch (error) {
      console.error('Error searching posts:', error);
    }
  },

  // Clear search
  clearSearch: () => {
    set({ searchResults: [], selectedPostId: null });
  },

  // Jump to specific date
  jumpToDate: async (accessToken, date, profileId) => {
    try {
      const url = `/api/posts/jump-to-date?date=${date}&profile_id=${profileId}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      const data = await response.json();
      if (data.success) {
        set({
          posts: data.posts,
          selectedDate: date,
          selectedPostId: null,
          offset: data.posts.length,
          hasMore: data.has_more
        });
      }
    } catch (error) {
      console.error('Error jumping to date:', error);
    }
  },

  // Jump to specific post
  jumpToPost: async (accessToken, postId, profileId, isReply = false) => {
    try {
      const url = `/api/posts/jump-to-post?post_id=${postId}&profile_id=${profileId}`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      const data = await response.json();
      if (data.success) {
        set({
          posts: data.posts,
          selectedPostId: postId,
          selectedDate: null,
          selectedPostHasReply: isReply,
          offset: data.posts.length,
          hasMore: data.has_more
        });
      }
    } catch (error) {
      console.error('Error jumping to post:', error);
    }
  },

  // Reset to latest posts
  resetToLatest: async (accessToken, profileId) => {
    set({
      selectedDate: null,
      selectedPostId: null,
      selectedPostHasReply: false,
      searchResults: [],
      posts: [],
      offset: 0,
      hasMore: true
    });
    await get().fetchPosts(accessToken, true, profileId);
  }
}));
